/**
 * Capitalizes the first letter of each word in a string.
 * Returns empty string if input is empty or undefined.
 */
export function capitalizeWords(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Converts a string to Title Case, lowercasing the rest of each word.
 * Unlike capitalizeWords (which only uppercases the first letter and leaves
 * the rest untouched), this normalizes ALL-CAPS input — e.g. "EDMONTON NORTH"
 * → "Edmonton North". Returns empty string if input is empty or undefined.
 */
export function toTitleCase(str: string | undefined | null): string {
  if (!str) return '';
  // Match the first char after start / whitespace / hyphen rather than \b\w,
  // which is ASCII-only and mishandles accented initials (e.g. "MONTRÉAL").
  return str
    .toLowerCase()
    .replace(/(^|[\s-])[^\s-]/g, (segment) => segment.toUpperCase());
}

/**
 * Converts a string to a URL-friendly slug.
 * - Converts to lowercase
 * - Replaces spaces and special characters with hyphens
 * - Removes consecutive hyphens
 * - Trims leading/trailing hyphens
 *
 * @example
 * slugify("Acme Corp 2024 Rebate Agreement") // "acme-corp-2024-rebate-agreement"
 * slugify("Q1 2024 - 5% Volume Rebate") // "q1-2024-5-volume-rebate"
 */
export function slugify(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Trim hyphens from start and end
}
